import type {
  ActionResult,
  LabelConfig,
  LabelFormat,
  SlidersFeatureConfig,
  TransitionProfile,
  WidgetState,
} from '@examdojo/core/widget';
import {
  DEFAULT_DISPLAY_CONFIG,
  DEFAULT_JSXGRAPH_BEHAVIOR_CONFIG,
  DEFAULT_JSXGRAPH_FEATURES,
  JsxgraphBaseWidget,
} from '@examdojo/core/widget';
import type { ThemeColorName } from '@examdojo/styles';
import type { GeometryElement } from 'jsxgraph';
import { QUADRATIC_FUNCTION_METADATA } from './quadratic-function.metadata';
import {
  DEFAULT_FEATURE_COLORS,
  DEFAULT_QUADRATIC_PARAMS,
  ParabolaStyle,
  QuadraticActionType,
  QuadraticBehaviorConfig,
  QuadraticDisplayConfig,
  QuadraticFeatureConfig,
  QuadraticWidgetConfig,
} from './quadratic-function.model';

/**
 * Widget for exploring quadratic functions y = ax² + bx + c
 */
export class QuadraticFunctionWidget extends JsxgraphBaseWidget<QuadraticWidgetConfig> {
  constructor(container: HTMLElement, jsxInstance: unknown, themeService: unknown) {
    super(container, jsxInstance as never, themeService as never);
    this.id = `quadratic-function-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
  }

  /** Threshold for detecting near-zero coefficient 'a' (linear vs quadratic) */
  private static readonly NEAR_ZERO_THRESHOLD = 0.01;

  static readonly metadata = QUADRATIC_FUNCTION_METADATA;

  readonly id: string;
  readonly type = 'quadratic-function';
  private config?: QuadraticWidgetConfig;
  private sliders: Map<string, GeometryElement> = new Map();
  private coefficients = { a: 1, b: 0, c: 0 };

  protected getCurrentConfig(): QuadraticWidgetConfig | undefined {
    return this.config;
  }

  getState(): WidgetState {
    const config = this.config;
    if (!config) {
      return { type: this.type };
    }

    // Return flat structure that matches init() expectations
    return {
      type: this.type,
      id: this.id,
      params: {
        ...config.params,
        a: this.getCoefficient('a'),
        b: this.getCoefficient('b'),
        c: this.getCoefficient('c'),
      },
      display: config.display,
      behavior: config.behavior,
    };
  }

  init(config: QuadraticWidgetConfig): void {
    // Clear sliders from previous initialization to avoid stale references
    this.sliders.clear();

    // 1. Synthesize Config
    const quadraticConfig = config;

    const params = { ...DEFAULT_QUADRATIC_PARAMS, ...(quadraticConfig.params || {}) };

    // Merge Display and Behavior with defaults
    // Note: normalizeConfig is for individual features that can be boolean.
    // The top-level containers are objects.
    const display = {
      ...DEFAULT_DISPLAY_CONFIG,
      ...(quadraticConfig.display || {}),
    } as QuadraticDisplayConfig;

    // Merge features with defaults
    const features = {
      ...DEFAULT_JSXGRAPH_FEATURES,
      ...(quadraticConfig.behavior?.features || {}),
    };

    const behavior: QuadraticBehaviorConfig = {
      ...DEFAULT_JSXGRAPH_BEHAVIOR_CONFIG,
      ...(quadraticConfig.behavior || {}),
      features,
    };

    // Store instance-level transitions from config
    if (behavior.transitions) {
      this.instanceTransitions = { ...behavior.transitions };
    }

    this.config = {
      ...quadraticConfig,
      params,
      display,
      behavior,
    };

    this.coefficients = {
      a: this.config.params.a!,
      b: this.config.params.b!,
      c: this.config.params.c!,
    };

    // Always calculate optimal boundingbox based on coefficients
    const boundingbox = this.calculateOptimalBoundingBox(this.coefficients.a, this.coefficients.b, this.coefficients.c);

    this.initBoard({
      boundingbox,
      axis: false,
    });

    // Apply Display Configs
    // createGrid must be called BEFORE createAxes (it sets the gridEnabled flag)
    this.createGrid(this.config.display.grid);

    if (this.config.display.axes !== false) {
      this.createAxes(this.config.display.axes);
    }

    // Apply Behavior Configs (from features)
    if (this.config.behavior?.features?.sliders) {
      this.createQuadraticSliders(this.config.behavior.features.sliders);
    }

    // Create parabola
    this.createParabola(this.config.display.parabola);

    // Create optional features
    if (this.config.display.vertex) {
      this.createVertex(this.config.display.vertex);
    }

    if (this.config.display.roots) {
      this.createRoots(this.config.display.roots);
    }

    if (this.config.display.axisOfSymmetry) {
      this.createAxisOfSymmetry(this.config.display.axisOfSymmetry);
    }

    if (this.config.display.yIntercept) {
      this.createYIntercept(this.config.display.yIntercept);
    }

    // Wire event handlers from behavior config
    if (this.config.behavior?.events) {
      this.wireEvents(this.config.behavior.events);
    }
  }

  private createQuadraticSliders(sliderConfig: boolean | SlidersFeatureConfig): void {
    const defaultParameters = ['a', 'b', 'c'];

    // If config is boolean true, use default parameters. If false, don't show sliders.
    if (sliderConfig === false) {
      return;
    }

    const config =
      typeof sliderConfig === 'boolean'
        ? ({ enabled: true, parameters: defaultParameters } as SlidersFeatureConfig)
        : sliderConfig;

    if (!config.enabled) {
      return;
    }

    const parameters: Array<{ name: string; range: [number, number, number] }> = [];
    const enabledParams = config.parameters || defaultParameters;

    if (enabledParams.includes('a')) {
      parameters.push({ name: 'a', range: [-5, this.coefficients.a, 5] });
    }
    if (enabledParams.includes('b')) {
      parameters.push({ name: 'b', range: [-10, this.coefficients.b, 10] });
    }
    if (enabledParams.includes('c')) {
      parameters.push({ name: 'c', range: [-10, this.coefficients.c, 10] });
    }

    // Apply semantic colors from theme via individualStyles
    const skyColor = this.themeService.getColor('sky');
    const pinkColor = this.themeService.getColor('pink');
    const amberColor = this.themeService.getColor('amber');

    // Build default individual styles for quadratic parameters
    const defaultIndividualStyles: SlidersFeatureConfig['individualStyles'] = {
      a: {
        strokeColor: skyColor,
        highlightStrokeColor: skyColor,
        highline: { strokeColor: skyColor },
      },
      b: {
        strokeColor: pinkColor,
        highlightStrokeColor: pinkColor,
        highline: { strokeColor: pinkColor },
      },
      c: {
        strokeColor: amberColor,
        highlightStrokeColor: amberColor,
        highline: { strokeColor: amberColor },
      },
    };

    // Merge user-provided individual styles with defaults
    const mergedIndividualStyles = Object.fromEntries(
      enabledParams.map((param) => [param, { ...defaultIndividualStyles[param], ...config.individualStyles?.[param] }]),
    );

    // Use base class createSliders with merged config
    this.sliders = this.createSliders(parameters, {
      ...config,
      enabled: true,
      parameters: enabledParams,
      individualStyles: mergedIndividualStyles,
    });
  }

  private getCoefficient(name: 'a' | 'b' | 'c'): number {
    const slider = this.sliders.get(name);
    return slider ? this.getSliderValue(slider) : this.coefficients[name];
  }

  private createParabola(style?: ParabolaStyle | boolean): void {
    const lineStyle = this.getLineStyle();
    const parabolaStyle = typeof style === 'object' ? style : {};

    // Get x range from board's bounding box
    const bbox = this.board.getBoundingBox();
    const xMin = bbox[0];
    const xMax = bbox[2];

    const parabola = this.board.create(
      'functiongraph',
      [
        (x: number) => {
          const a = this.getCoefficient('a');
          const b = this.getCoefficient('b');
          const c = this.getCoefficient('c');
          return a * x * x + b * x + c;
        },
        xMin,
        xMax,
      ],
      {
        ...lineStyle,
        strokeWidth: parabolaStyle.strokeWidth || 3,
        dash: parabolaStyle.dashed ? 2 : 0,
      },
    );

    this.objects.set('parabola', parabola);
  }

  private normalizeFeatureConfig(config: boolean | QuadraticFeatureConfig): QuadraticFeatureConfig {
    if (typeof config === 'boolean') {
      return { enabled: config };
    }
    return { ...config, enabled: config.enabled !== false };
  }

  private createVertex(display: boolean | QuadraticFeatureConfig): void {
    const config = this.normalizeFeatureConfig(display);
    if (!config.enabled) {
      return;
    }

    const colorName = config.color ?? DEFAULT_FEATURE_COLORS.vertex;
    const size = 4;
    const lineColor = this.themeService.getColor(colorName);

    const vertex = this.board.create(
      'point',
      [
        () => {
          const a = this.getCoefficient('a');
          if (Math.abs(a) < QuadraticFunctionWidget.NEAR_ZERO_THRESHOLD) {
            return NaN; // No vertex for linear functions
          }
          const b = this.getCoefficient('b');
          return -b / (2 * a);
        },
        () => {
          const a = this.getCoefficient('a');
          if (Math.abs(a) < QuadraticFunctionWidget.NEAR_ZERO_THRESHOLD) {
            return NaN; // No vertex for linear functions
          }
          const b = this.getCoefficient('b');
          const c = this.getCoefficient('c');
          const h = -b / (2 * a);
          return a * h * h + b * h + c;
        },
      ],
      {
        name: '',
        size,
        fillColor: lineColor,
        strokeColor: lineColor,
        fixed: true,
        visible: true,
        withLabel: false,
      },
    ) as GeometryElement;

    // Create label with configurable format and variant
    const labelConfig = config.label;
    if (labelConfig?.show) {
      // Use function for dynamic labels when format includes coordinates
      const labelTextFn = () => this.formatFeatureLabel('vertex', vertex, labelConfig.format);
      const fullLabelConfig = this.buildLabelConfig(labelConfig, config.color ?? DEFAULT_FEATURE_COLORS.vertex);
      const label = this.createLabel(vertex, labelTextFn, fullLabelConfig);
      this.objects.set('vertex-label', label);
    }

    this.objects.set('vertex-point', vertex);
  }

  private createRoots(display: boolean | QuadraticFeatureConfig): void {
    const config = this.normalizeFeatureConfig(display);
    if (!config.enabled) {
      return;
    }

    const colorName = config.color ?? DEFAULT_FEATURE_COLORS.roots;
    const size = 3;
    const lineColor = this.themeService.getColor(colorName);

    // Root 1
    const root1 = this.board.create(
      'point',
      [
        () => {
          const a = this.getCoefficient('a');
          if (Math.abs(a) < QuadraticFunctionWidget.NEAR_ZERO_THRESHOLD) {
            return NaN; // No quadratic roots for linear functions
          }
          const b = this.getCoefficient('b');
          const c = this.getCoefficient('c');
          const discriminant = b * b - 4 * a * c;
          if (discriminant < 0) {
            return NaN;
          }
          return (-b - Math.sqrt(discriminant)) / (2 * a);
        },
        0,
      ],
      {
        name: '',
        size,
        fillColor: lineColor,
        strokeColor: lineColor,
        fixed: true,
        withLabel: false,
      },
    ) as GeometryElement;

    // Root 2
    const root2 = this.board.create(
      'point',
      [
        () => {
          const a = this.getCoefficient('a');
          if (Math.abs(a) < QuadraticFunctionWidget.NEAR_ZERO_THRESHOLD) {
            return NaN; // No quadratic roots for linear functions
          }
          const b = this.getCoefficient('b');
          const c = this.getCoefficient('c');
          const discriminant = b * b - 4 * a * c;
          if (discriminant < 0) {
            return NaN;
          }
          return (-b + Math.sqrt(discriminant)) / (2 * a);
        },
        0,
      ],
      {
        name: '',
        size,
        fillColor: lineColor,
        strokeColor: lineColor,
        fixed: true,
        withLabel: false,
      },
    ) as GeometryElement;

    // Create labels with configurable format and variant
    const labelConfig = config.label;
    if (labelConfig?.show) {
      // Use functions for dynamic labels when format includes coordinates
      const labelText1Fn = () => this.formatFeatureLabel('root1', root1, labelConfig.format);
      const labelText2Fn = () => this.formatFeatureLabel('root2', root2, labelConfig.format);
      const fullLabelConfig = this.buildLabelConfig(labelConfig, config.color ?? DEFAULT_FEATURE_COLORS.roots);
      const label1 = this.createLabel(root1, labelText1Fn, fullLabelConfig);
      const label2 = this.createLabel(root2, labelText2Fn, fullLabelConfig);
      this.objects.set('root-1-label', label1);
      this.objects.set('root-2-label', label2);
    }

    this.objects.set('root-1', root1);
    this.objects.set('root-2', root2);
  }

  private createAxisOfSymmetry(display: boolean | QuadraticFeatureConfig): void {
    const config = this.normalizeFeatureConfig(display);
    if (!config.enabled) {
      return;
    }

    const colorName = config.color ?? DEFAULT_FEATURE_COLORS.axisOfSymmetry;
    const lineStyle = this.getLineStyle({ colorName, dash: 2 });

    // Get y range from board's bounding box
    const bbox = this.board.getBoundingBox();
    const yMin = bbox[3];
    const yMax = bbox[1];

    const axis = this.board.create(
      'line',
      [
        [
          () => {
            const a = this.getCoefficient('a');
            if (Math.abs(a) < QuadraticFunctionWidget.NEAR_ZERO_THRESHOLD) {
              return NaN; // No axis of symmetry for linear functions
            }
            const b = this.getCoefficient('b');
            return -b / (2 * a);
          },
          yMin,
        ],
        [
          () => {
            const a = this.getCoefficient('a');
            if (Math.abs(a) < QuadraticFunctionWidget.NEAR_ZERO_THRESHOLD) {
              return NaN; // No axis of symmetry for linear functions
            }
            const b = this.getCoefficient('b');
            return -b / (2 * a);
          },
          yMax,
        ],
      ],
      {
        ...lineStyle,
        name: '',
        strokeWidth: 1,
        straightFirst: false,
        straightLast: false,
        withLabel: false,
      },
    );

    // Create a point on the line for the label
    const labelPoint = this.board.create(
      'point',
      [
        () => {
          const a = this.getCoefficient('a');
          if (Math.abs(a) < QuadraticFunctionWidget.NEAR_ZERO_THRESHOLD) {
            return NaN; // No axis of symmetry for linear functions
          }
          const b = this.getCoefficient('b');
          return -b / (2 * a);
        },
        () => yMax - 1,
      ],
      {
        visible: false,
        fixed: true,
        withLabel: false,
      },
    );

    // Create label with configurable format and variant
    const labelConfig = config.label;
    if (labelConfig?.show) {
      // Use function for dynamic labels when format includes coordinates
      const labelTextFn = () => this.formatFeatureLabel('axisOfSymmetry', labelPoint, labelConfig.format);
      const fullLabelConfig = this.buildLabelConfig(labelConfig, config.color ?? DEFAULT_FEATURE_COLORS.axisOfSymmetry);
      const label = this.createLabel(labelPoint, labelTextFn, fullLabelConfig, [-25, 10]);
      this.objects.set('axis-label', label);
    }

    this.objects.set('axis-line', axis);
    this.objects.set('axis-label-point', labelPoint);
  }

  private createYIntercept(display: boolean | QuadraticFeatureConfig): void {
    const config = this.normalizeFeatureConfig(display);
    if (!config.enabled) {
      return;
    }

    const colorName = config.color ?? DEFAULT_FEATURE_COLORS.yIntercept;
    const size = 4;
    const lineColor = this.themeService.getColor(colorName);

    const yIntercept = this.board.create(
      'point',
      [
        0,
        () => this.getCoefficient('c'), // y-intercept is always c when x=0
      ],
      {
        name: '',
        size,
        fillColor: lineColor,
        strokeColor: lineColor,
        fixed: true,
        withLabel: false,
      },
    ) as GeometryElement;

    // Create label with configurable format and variant
    const labelConfig = config.label;
    if (labelConfig?.show) {
      // Use function for dynamic labels when format includes coordinates
      const labelTextFn = () => this.formatFeatureLabel('yIntercept', yIntercept, labelConfig.format);
      const fullLabelConfig = this.buildLabelConfig(labelConfig, config.color ?? DEFAULT_FEATURE_COLORS.yIntercept);
      const label = this.createLabel(yIntercept, labelTextFn, fullLabelConfig);
      this.objects.set('y-intercept-label', label);
    }

    this.objects.set('y-intercept-point', yIntercept);
  }

  private buildLabelConfig(labelConfig: LabelConfig, defaultColor: ThemeColorName): LabelConfig {
    return {
      ...labelConfig,
      variant: labelConfig.variant ?? 'secondary',
      color: labelConfig.color ?? defaultColor,
    };
  }

  private formatFeatureLabel(
    featureType: 'vertex' | 'root1' | 'root2' | 'axisOfSymmetry' | 'yIntercept',
    element: GeometryElement,
    format: LabelFormat = 'name',
  ): string {
    const getX = () => (element as unknown as { X: () => number }).X();
    const getY = () => (element as unknown as { Y: () => number }).Y();

    const featureNames: Record<typeof featureType, string> = {
      vertex: 'Vertex',
      root1: 'Root',
      root2: 'Root',
      axisOfSymmetry: 'Axis of Symmetry',
      yIntercept: 'Y-Intercept',
    };

    const name = featureNames[featureType];

    if (format === 'name') {
      return name;
    }

    // Format coordinates
    const formatNum = (n: number): string => {
      if (!isFinite(n)) {
        return 'N/A';
      }
      return Math.abs(n) < 0.01 ? '0' : n.toFixed(2);
    };

    let coordinate = '';
    if (featureType === 'axisOfSymmetry') {
      // Axis of symmetry uses x= format
      coordinate = `x = ${formatNum(getX())}`;
    } else {
      // Other features use (x, y) format
      coordinate = `(${formatNum(getX())}, ${formatNum(getY())})`;
    }

    if (format === 'coordinate') {
      return coordinate;
    }

    // format === 'nameAndCoordinate'
    return `${name} ${coordinate}`;
  }

  protected override getRegionBoundingBox(region: string | number): [number, number, number, number] | null {
    const a = this.getCoefficient('a');
    const b = this.getCoefficient('b');
    const c = this.getCoefficient('c');

    // For near-zero 'a', the function is linear - only 'all' region makes sense
    if (Math.abs(a) < QuadraticFunctionWidget.NEAR_ZERO_THRESHOLD) {
      return region === 'all' ? this.calculateOptimalBoundingBox(a, b, c) : null;
    }

    const h = -b / (2 * a);
    const k = a * h * h + b * h + c;

    if (!isFinite(h) || !isFinite(k)) {
      return null;
    }

    switch (region) {
      case 'vertex':
        return [h - 3, k + 3, h + 3, k - 3];
      case 'roots': {
        const discriminant = b * b - 4 * a * c;
        if (discriminant >= 0) {
          const r1 = (-b - Math.sqrt(discriminant)) / (2 * a);
          const r2 = (-b + Math.sqrt(discriminant)) / (2 * a);
          if (isFinite(r1) && isFinite(r2)) {
            const xMin = Math.min(r1, r2) - 2;
            const xMax = Math.max(r1, r2) + 2;
            return [xMin, 2, xMax, -2];
          }
        }
        return null; // No real roots or invalid values
      }
      case 'axisOfSymmetry':
        return [h - 2, k + 4, h + 2, k - 4];
      case 'yIntercept':
        return [-2, c + 3, 2, c - 3];
      case 'parabola':
      case 'all':
        return this.calculateOptimalBoundingBox(a, b, c);
      default:
        return null; // Unknown region
    }
  }

  /**
   * Override performAction to handle quadratic-specific actions.
   */
  protected override async performAction(
    action: QuadraticActionType,
    payload?: Record<string, unknown>,
    transition?: TransitionProfile,
  ): Promise<ActionResult> {
    switch (action) {
      case 'UPDATE_COEFFICIENTS':
        return this.actionUpdateCoefficients(payload, transition);
      case 'FIT_TO_PARABOLA':
        return this.actionFitToParabola(transition);
      default:
        return super.performAction(action, payload, transition);
    }
  }

  /**
   * Update a quadratic coefficient with optional animation.
   * Payload format: { param: 'a' | 'b' | 'c', value: number }
   */
  private async actionUpdateCoefficients(
    payload?: Record<string, unknown>,
    transition?: TransitionProfile,
  ): Promise<ActionResult> {
    if (!this.board) {
      return { success: false, error: 'Board not initialized' };
    }

    if (!payload || !('param' in payload) || !('value' in payload)) {
      return { success: false, error: 'Missing param or value in payload' };
    }

    const param = payload['param'] as 'a' | 'b' | 'c';
    const value = payload['value'] as number;

    if (!['a', 'b', 'c'].includes(param)) {
      return { success: false, error: `Invalid param '${param}'. Must be 'a', 'b', or 'c'` };
    }

    // For instant transitions, update directly
    if (!transition || transition.type === 'none' || transition.duration === 0) {
      const slider = this.sliders.get(param);
      if (slider) {
        this.setSliderValue(slider, value);
      } else {
        this.coefficients[param] = value;
      }
      this.board.update();
      this.updateLabels();
      return { success: true };
    }

    // Animated coefficient update
    const startValue = this.getCoefficient(param);
    const endValue = value;

    const steps = Math.max(1, Math.round(transition.duration / 16));
    const stepDuration = transition.duration / steps;
    const easingFn = this.getEasingFunction(transition.easing);

    for (let step = 1; step <= steps; step++) {
      // Check if board was destroyed during animation
      if (!this.board) {
        return { success: false, error: 'Board destroyed during animation' };
      }

      const progress = easingFn(step / steps);
      const interpolated = startValue + (endValue - startValue) * progress;

      const slider = this.sliders.get(param);
      if (slider) {
        this.setSliderValue(slider, interpolated);
      } else {
        this.coefficients[param] = interpolated;
      }

      this.board.update();
      this.updateLabels();

      if (step < steps) {
        await new Promise((resolve) => setTimeout(resolve, stepDuration));
      }
    }
    return { success: true };
  }

  /**
   * Auto-fit the view to show the entire parabola with appropriate padding.
   */
  private async actionFitToParabola(transition?: TransitionProfile): Promise<ActionResult> {
    if (!this.board) {
      return { success: false, error: 'Board not initialized' };
    }

    const a = this.getCoefficient('a');
    const b = this.getCoefficient('b');
    const c = this.getCoefficient('c');

    const bbox = this.calculateOptimalBoundingBox(a, b, c);
    await this.animateBoundingBox(bbox, transition);
    return { success: true };
  }

  /**
   * Calculate optimal bounding box based on quadratic coefficients.
   * Ensures vertex, roots, and y-intercept are visible with appropriate padding.
   */
  private calculateOptimalBoundingBox(a: number, b: number, c: number): [number, number, number, number] {
    // Handle edge case where a ≈ 0 (not a parabola)
    if (Math.abs(a) < QuadraticFunctionWidget.NEAR_ZERO_THRESHOLD) {
      return [-10, 10, 10, -10];
    }

    // Calculate vertex
    const h = -b / (2 * a);
    const k = a * h * h + b * h + c;

    // Calculate discriminant for roots
    const discriminant = b * b - 4 * a * c;

    // Start with vertex and y-intercept
    let xMin = Math.min(h, 0);
    let xMax = Math.max(h, 0);
    let yMin = Math.min(k, c);
    let yMax = Math.max(k, c);

    // Include roots if they exist
    if (discriminant >= 0) {
      const r1 = (-b - Math.sqrt(discriminant)) / (2 * a);
      const r2 = (-b + Math.sqrt(discriminant)) / (2 * a);
      xMin = Math.min(xMin, r1, r2);
      xMax = Math.max(xMax, r1, r2);
      // Roots are at y=0, so include 0 in the y range
      yMin = Math.min(yMin, 0);
      yMax = Math.max(yMax, 0);
    }

    // Add padding (30% margins)
    const xPadding = Math.max((xMax - xMin) * 0.3, 2);
    const yPadding = Math.max((yMax - yMin) * 0.3, 2);

    xMin -= xPadding;
    xMax += xPadding;
    yMin -= yPadding;
    yMax += yPadding;

    // Ensure minimum visible area (10x10 units)
    const minWidth = 10;
    const minHeight = 10;

    if (xMax - xMin < minWidth) {
      const center = (xMin + xMax) / 2;
      xMin = center - minWidth / 2;
      xMax = center + minWidth / 2;
    }

    if (yMax - yMin < minHeight) {
      const center = (yMin + yMax) / 2;
      yMin = center - minHeight / 2;
      yMax = center + minHeight / 2;
    }

    // Round to nice numbers for cleaner display
    xMin = Math.floor(xMin);
    xMax = Math.ceil(xMax);
    yMin = Math.floor(yMin);
    yMax = Math.ceil(yMax);

    // JSXGraph boundingbox format: [xMin, yMax, xMax, yMin]
    return [xMin, yMax, xMax, yMin];
  }
}
